import React, { useState } from 'react'
import { Container } from './OrderDetailedView.style'
import TransactionOverview from '@components/TransactionOverview/TransactionOverview.component'
import Badge from '@components/Badge/Badge.component'
import Link from '@components/Link/Link.component'
import { useStyletron } from 'parkdepot-shared/theme'
import { formatPriceWithCurrency } from '@utils/formatPriceWithCurrency.utils'
import { priceToString } from '@utils/priceToString.utils'
import { DateTime } from 'luxon'
import { useGetUser } from '@hooks/useAuth'
import { CARPAY_STATUS } from 'parkdepot-shared/models/database'
import { BadgeVariant } from '@components/Badge/types'
import useGetOrderInvoice from '@hooks/useGetOrderInvoice'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { isOrderSavedInPaidOrders } from '@utils/carpaySavedPaidOrders.utils'
import { B2CCarpayOrderView, SvLotType } from 'parkdepot-shared/gql/graphql'
import CarpayOrderActions from '@components/CarpayOrderActions/CarpayOrderActions.component'
import Drawer from '@components/Drawer/Drawer.component'
import PaymentSuccess from '@views/Carpay/PaymentFinished/components/PaymentSuccess/PaymentSuccess.component'
import { getRedirectUrl } from '@utils/getPaymentRedirectUrl.utils'

const getBadgeConfig = (orderId: string, status: string, t: TFunction) => {
    // If the order is saved in the session storage, it means it has been paid (could not yet be reflected on the backend due to adyen async flow)
    const isOrderSavedOnSavedOrders = isOrderSavedInPaidOrders(orderId)
    let statusCp = status
    if (isOrderSavedOnSavedOrders) statusCp = CARPAY_STATUS.PAID

    let badgeVariant: BadgeVariant = 'default'
    let badgeText = ''
    switch (statusCp) {
        case CARPAY_STATUS.PAID:
            badgeVariant = 'information'
            badgeText = t('carpay.orderStatuses.paid')
            break
        case CARPAY_STATUS.INCOMPLETE:
            badgeVariant = 'positive'
            badgeText = t('carpay.orderStatuses.active')
            break
        case CARPAY_STATUS.UNPAID:
            badgeVariant = 'warning'
            badgeText = t('carpay.orderStatuses.unpaid')
            break
        case CARPAY_STATUS.UNPAID_ENFORCEABLE:
            badgeVariant = 'negative'
            badgeText = t('carpay.orderStatuses.unpaidEnforceable')
            break
        default:
            badgeVariant = 'default'
            badgeText = t('carpay.orderStatuses.active')
            break
    }

    return { badgeVariant, badgeText }
}

const OrderDetailedView = ({
    selectedOrder,
    onBackClick
}: {
    selectedOrder: B2CCarpayOrderView
    onBackClick?: () => void
}) => {
    const { user } = useGetUser()
    const { t } = useTranslation()
    const [css, theme] = useStyletron()
    const [openedModal, setOpenedModal] = useState(() => {
        return new URLSearchParams(window.location.search).get('status') !== null
    })

    const { price, order, lot } = selectedOrder

    const [getOrderInvoice] = useGetOrderInvoice({
        variables: {
            order_id: order.order_id
        },
        onCompleted: (data) => {
            window.open(data.b2c_get_invoice.url, '_blank')
        },
        onError: () => {
            // TODO: show error message - toast?
        }
    })

    const now = DateTime.now()
    const plate = order.plate
    const startDate = DateTime.fromISO(order.start_date)
    const endDate = DateTime.fromISO(order.end_date ?? now.toISO())
    const priceValidUntil = DateTime.fromISO(price.price_valid_until)
    const currency = price.currency
    const paidPrice = price.discounted_parking_price
    const vat = price.vat_rate * 100
    const orderId = order.order_id
    const vatValue = formatPriceWithCurrency(currency).format(
        priceToString(paidPrice - paidPrice / (1 + price.vat_rate))
    )
    const duration = endDate.diff(startDate).shiftTo('hours', 'minutes', 'seconds').toFormat("h'h' m'm' s's'")
    const vehicle = (user?.vehicles ?? []).find((v) => v.plate === plate || v.plate?.replaceAll('_', '') === plate)
    const vehicleName = vehicle ? `${vehicle.name}, ${vehicle.plate?.replaceAll('_', '-')}` : ''
    const parkingAddress = lot.address.split(',').map((part) => part.trim())
    const formattedAddress = (
        <div className={css({ whiteSpace: 'pre-line' })}>
            {lot.name}
            {'\n'}
            {parkingAddress.join('\n')}
        </div>
    )
    const { badgeText, badgeVariant } = getBadgeConfig(order.order_id, order.status, t)

    const isPaidOrder = order.status === 'PAID'
    const country = new URLSearchParams(window.location.search).get('country') || 'INT'
    const redirectUrl = getRedirectUrl(user, plate, country)

    return (
        <Container>
            <TransactionOverview title={t('carpay.orderDetail.overview')} onBackClick={onBackClick}>
                <TransactionOverview.InfoContainer
                    className={css({ paddingBottom: theme.sizing.scale700 })}
                    divider={true}
                >
                    <TransactionOverview.Subtitle>{t('carpay.orderDetail.detail')}</TransactionOverview.Subtitle>

                    <TransactionOverview.InfoItem>
                        <span>{t('carpay.orderDetail.status')}</span>
                        <Badge variant={badgeVariant} text={badgeText} dataTestId="order-status" />
                    </TransactionOverview.InfoItem>

                    <TransactionOverview.InfoItem>
                        <span>{t('carpay.orderDetail.vehicle')}</span>
                        <span>{vehicleName || plate}</span>
                    </TransactionOverview.InfoItem>

                    {isPaidOrder && (
                        <>
                            <TransactionOverview.InfoItem>
                                <span>{t('carpay.orderDetail.entry')}</span>
                                <span>{startDate.toFormat('dd.MM.yyyy - HH:mm')}</span>
                            </TransactionOverview.InfoItem>

                            <TransactionOverview.InfoItem>
                                <span>{t('carpay.orderDetail.exit')}</span>
                                <span>{endDate.toFormat('dd.MM.yyyy - HH:mm')}</span>
                            </TransactionOverview.InfoItem>
                        </>
                    )}

                    <TransactionOverview.InfoItem>
                        <span>{t('carpay.orderDetail.duration')}</span>
                        <span>{duration}</span>
                    </TransactionOverview.InfoItem>

                    {isPaidOrder && (
                        <TransactionOverview.InfoItem>
                            <span>{t('carpay.orderDetail.validUntil')}</span>
                            <span>{priceValidUntil.toFormat('dd.MM.yyyy - HH:mm')}</span>
                        </TransactionOverview.InfoItem>
                    )}

                    <TransactionOverview.InfoItem>
                        <span>{t('carpay.orderDetail.orderId')}</span>
                        <span>{orderId}</span>
                    </TransactionOverview.InfoItem>

                    <TransactionOverview.InfoItem>
                        <TransactionOverview.Subtitle>{t('carpay.orderDetail.price')}</TransactionOverview.Subtitle>
                        <TransactionOverview.PriceContainer>
                            <TransactionOverview.Price>
                                <span data-testid="carpay-order-price">
                                    {price ? formatPriceWithCurrency(currency).format(priceToString(paidPrice)) : '- €'}
                                </span>
                            </TransactionOverview.Price>
                            <TransactionOverview.PriceVat>
                                inkl. {vat.toFixed(2)}% VAT: {vatValue}
                            </TransactionOverview.PriceVat>
                        </TransactionOverview.PriceContainer>
                    </TransactionOverview.InfoItem>

                    {isPaidOrder && (
                        <Link className={css({ alignSelf: 'flex-end', fontSize: '1.4rem' })} onClick={getOrderInvoice}>
                            {t('carpay.orderDetail.downloadInvoice')}
                        </Link>
                    )}
                </TransactionOverview.InfoContainer>

                <CarpayOrderActions orderData={selectedOrder} />

                {isPaidOrder && (
                    <TransactionOverview.InfoContainer className={css({ paddingTop: theme.sizing.scale700 })}>
                        <TransactionOverview.Subtitle>
                            {t('carpay.orderDetail.parkingLotInformation')}
                        </TransactionOverview.Subtitle>
                        <TransactionOverview.InfoItem className={css({ flexDirection: 'column' })}>
                            <span className={css({ fontWeight: 700 })}>{t('carpay.orderDetail.parkingLot')}</span>
                            <div>{formattedAddress}</div>
                        </TransactionOverview.InfoItem>
                    </TransactionOverview.InfoContainer>
                )}
            </TransactionOverview>
            <Drawer
                isOpen={openedModal}
                onClose={() => setOpenedModal(false)}
                content={
                    <PaymentSuccess
                        price={paidPrice}
                        plate={plate}
                        now={now}
                        priceValidUntil={priceValidUntil.toISO() || ''}
                        isTollroad={lot.sv_lot_type === SvLotType.TollRoad}
                        nextOrderId={null}
                        redirectUrl={redirectUrl}
                    />
                }
            />
        </Container>
    )
}

export default OrderDetailedView
